import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit3, Trash2, MapPin, Users, TrendingUp, Filter, Eye, Building2 } from 'lucide-react';
import { propertyService } from '../../services/propertyService';
import { communeService } from '../../services/communeService';
import { useAuth } from '../../contexts/AuthContext';
import CommuneFormModal from './components/CommuneFormModal';
import PropertyList from './components/PropertyList';
import CommuneStats from './components/CommuneStats';

const CommuneManagementDashboard = () => {
  const { user, userProfile } = useAuth();
  const [communes, setCommunes] = useState([]);
  const [selectedCommune, setSelectedCommune] = useState(null);
  const [communeProperties, setCommuneProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDistrict, setFilterDistrict] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingCommune, setEditingCommune] = useState(null);
  const [stats, setStats] = useState(null);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [error, setError] = useState('');

  // Load communes with property counts
  const loadCommunes = async () => {
    setLoading(true);
    try {
      const response = await communeService?.getCommunesWithPropertyCount();
      
      if (response?.error) {
        setError(response?.error);
        setCommunes([]);
        return;
      }
      
      const communesData = response?.data || [];
      setCommunes(communesData);
      
      // Select first commune by default
      if (communesData?.length > 0 && !selectedCommune) {
        setSelectedCommune(communesData?.[0]);
      }
    } catch (error) {
      setError('Erreur lors du chargement des communes');
      console.error('Error loading communes:', error);
      setCommunes([]);
    } finally {
      setLoading(false);
    }
  };

  // Load properties for selected commune
  const loadCommuneProperties = async (commune) => {
    if (!commune) return;
    
    setPropertyLoading(true);
    try {
      const response = await propertyService?.getPropertiesByCommune(commune?.id);
      
      if (response?.error) {
        console.error('Error loading commune properties:', response?.error);
        setCommuneProperties([]);
        return;
      }
      
      setCommuneProperties(response?.data || []);
    } catch (error) {
      console.error('Error loading commune properties:', error);
      setCommuneProperties([]);
    } finally {
      setPropertyLoading(false);
    }
  };

  // Load statistics
  const loadStats = async () => {
    try {
      const [communeStatsResponse, propertyStatsResponse] = await Promise.all([
        communeService?.getCommuneStats(),
        propertyService?.getPropertyStats()
      ]);
      
      const communeStats = communeStatsResponse?.error ? null : communeStatsResponse?.data;
      const propertyStats = propertyStatsResponse?.error ? null : propertyStatsResponse?.data;
      
      if (communeStatsResponse?.error) {
        console.error('Error loading commune stats:', communeStatsResponse?.error);
      }
      
      if (propertyStatsResponse?.error) {
        console.error('Error loading property stats:', propertyStatsResponse?.error);
      }
      
      setStats({ commune: communeStats, property: propertyStats });
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({ commune: null, property: null });
    }
  };

  useEffect(() => {
    loadCommunes();
    loadStats();
  }, []);

  useEffect(() => {
    if (selectedCommune) {
      loadCommuneProperties(selectedCommune);
    }
  }, [selectedCommune]);

  // Handle commune selection
  const handleCommuneSelect = (commune) => {
    setSelectedCommune(commune);
  };

  // Handle commune create/update
  const handleCommuneSave = async (communeData) => {
    try {
      let result;
      if (editingCommune) {
        result = await communeService?.updateCommune(editingCommune?.id, communeData);
      } else {
        result = await communeService?.createCommune(communeData);
      }

      if (result?.error) {
        setError(result?.error);
        return false;
      }

      await loadCommunes();
      setShowFormModal(false);
      setEditingCommune(null);
      
      // Update selected commune if it was edited
      if (editingCommune && selectedCommune?.id === editingCommune?.id) {
        setSelectedCommune(result?.data);
      }
      
      return true;
    } catch (error) {
      setError('Erreur lors de la sauvegarde de la commune');
      console.error('Error saving commune:', error);
      return false;
    }
  };

  // Handle commune delete
  const handleCommuneDelete = async (commune) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la commune "${commune?.name}" ?`)) {
      return;
    }

    try {
      let result = await communeService?.deleteCommune(commune?.id);
      
      if (result?.error) {
        setError(result?.error);
        return;
      }

      await loadCommunes();
      
      // Clear selection if deleted commune was selected
      if (selectedCommune?.id === commune?.id) {
        setSelectedCommune(null);
        setCommuneProperties([]);
      }
    } catch (error) {
      setError('Erreur lors de la suppression de la commune');
      console.error('Error deleting commune:', error);
    }
  };

  // Filter communes - ensure communes is always an array
  const filteredCommunes = (communes || [])?.filter(commune => {
    const matchesSearch = commune?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) || 
                         commune?.district?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesDistrict = !filterDistrict || commune?.district === filterDistrict;
    return matchesSearch && matchesDistrict;
  });

  // Get unique districts - ensure communes is always an array
  const districts = [...new Set((communes || [])?.map(c => c?.district)?.filter(Boolean))];

  // Check if user is admin
  const isAdmin = userProfile?.role === 'admin';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du tableau de bord des communes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <MapPin className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestion des Communes</h1>
                <p className="text-sm text-gray-600">Administration centralisée des communes d'Abidjan</p>
              </div>
            </div>
            
            {isAdmin && (
              <button
                onClick={() => {
                  setEditingCommune(null);
                  setShowFormModal(true);
                }}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle Commune
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Error Display */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
            <button 
              onClick={() => setError('')}
              className="float-right font-bold text-red-700 hover:text-red-900"
            >
              ×
            </button>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Statistics */}
        {stats && <CommuneStats stats={stats} />}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          {/* Communes Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-4 border-b">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Communes ({filteredCommunes?.length})</h2>
                
                {/* Search and Filter */}
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <select
                    value={filterDistrict}
                    onChange={(e) => setFilterDistrict(e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tous les districts</option>
                    {districts?.map(district => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Communes List */}
              <div className="max-h-96 overflow-y-auto">
                {filteredCommunes?.map(commune => (
                  <div
                    key={commune?.id}
                    onClick={() => handleCommuneSelect(commune)}
                    className={`p-4 border-b cursor-pointer transition-colors ${
                      selectedCommune?.id === commune?.id 
                        ? 'bg-blue-50 border-blue-200' :'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{commune?.name}</h3>
                        <p className="text-sm text-gray-600">{commune?.district}</p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center">
                            <Users className="h-3 w-3 mr-1" />
                            {commune?.population?.toLocaleString() || 'N/A'}
                          </span>
                          <span className="flex items-center">
                            <Building2 className="h-3 w-3 mr-1" />
                            {commune?.propertyCount || 0} propriétés
                          </span>
                        </div>
                      </div>
                      
                      {isAdmin && (
                        <div className="flex items-center space-x-2 ml-2">
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              setEditingCommune(commune);
                              setShowFormModal(true);
                            }}
                            className="p-1 text-gray-400 hover:text-blue-600 rounded"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              handleCommuneDelete(commune);
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {filteredCommunes?.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <MapPin className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p>Aucune commune trouvée</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            {selectedCommune ? (
              <div className="space-y-6">
                {/* Commune Details */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">{selectedCommune?.name}</h2>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {selectedCommune?.district}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <Users className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-gray-900">
                        {selectedCommune?.population?.toLocaleString() || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-600">Population</p>
                    </div>
                    
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <Building2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-gray-900">
                        {selectedCommune?.propertyCount || 0}
                      </p>
                      <p className="text-sm text-gray-600">Propriétés</p>
                    </div>
                    
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <TrendingUp className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-gray-900">
                        {selectedCommune?.average_price ? 
                          `${(selectedCommune?.average_price / 1000000)?.toFixed(1)}M` : 'N/A'}
                      </p>
                      <p className="text-sm text-gray-600">Prix moyen (FCFA)</p>
                    </div>
                  </div>

                  {selectedCommune?.description && (
                    <div className="mb-4">
                      <h3 className="font-medium text-gray-900 mb-2">Description</h3>
                      <p className="text-gray-700">{selectedCommune?.description}</p>
                    </div>
                  )}

                  {selectedCommune?.amenities && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">Commodités</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        {Object.entries(selectedCommune?.amenities)?.map(([key, value]) => {
                          if (Array.isArray(value)) {
                            return (
                              <div key={key} className="text-gray-700">
                                <span className="font-medium capitalize">{key?.replace('_', ' ')}: </span>
                                {value?.join(', ')}
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Properties Section */}
                <PropertyList 
                  properties={communeProperties} 
                  loading={propertyLoading}
                  commune={selectedCommune}
                  onPropertyUpdate={loadCommuneProperties}
                />
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <Eye className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Sélectionnez une commune</h3>
                <p className="text-gray-600">Choisissez une commune dans la liste pour voir ses détails et propriétés associées</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Commune Form Modal */}
      {showFormModal && (
        <CommuneFormModal
          commune={editingCommune}
          onSave={handleCommuneSave}
          onClose={() => {
            setShowFormModal(false);
            setEditingCommune(null);
          }}
        />
      )}
    </div>
  );
};

export default CommuneManagementDashboard;